import React, { useContext, useEffect, useState } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import TabContext from "../TabContext";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

function WoInfotipData({ infotipData, endIndex, loading, currentPage, itemsPerPage }) {
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const { openTab } = useContext(TabContext);

  useEffect(() => {
    const workshop = localStorage.getItem('workshop');
    const asp = localStorage.getItem('ASC');
    const usertype = localStorage.getItem('usertype');

    const extractPlannedDate = (workOrder) => {
      try {
        if (!workOrder || !Array.isArray(workOrder.history)) {
          console.warn("Invalid or missing workOrder.history");
          return null;
        }
    
        const extractDate = (keyword) => {
          try {
            const entry = workOrder.history.find(item =>
              typeof item === 'string' && item.toLowerCase().includes(keyword.toLowerCase())
            );
            if (entry) {
              const match = entry.match(new RegExp(`${keyword}\\s*:?\\s*([\\d-]+)`, 'i'));
              if (match && match[1]) {
                const date = new Date(match[1]);
                return isNaN(date.getTime()) ? null : date;
              }
            }
          } catch (innerErr) {
            console.error(`Error extracting date for keyword "${keyword}":`, innerErr);
          }
          return null;
        };
    
        const homeVisitDate = extractDate("homevisitplanned date");
        const pickupRequestDate = extractDate("pickuprequest date");
        const pickupPlannedDate = extractDate("pickupplanned date");
    
        const validDates = [homeVisitDate, pickupRequestDate, pickupPlannedDate].filter(Boolean);
    
        if (validDates.length === 0) return null;
    
        const earliest = new Date(Math.min(...validDates.map(d => d.getTime())));
        return earliest.toISOString().split('T')[0];
    
      } catch (err) {
        console.error("Error in extractPlannedDate:", err);
        return null;
      }
    };
    
  
    const filtered = infotipData.filter(workOrder => {
      const workshopMatches = !workshop || workOrder.WorkshopName === workshop;
      const aspMatchesTPVTechnician = !workOrder.ASC || workOrder.ASC === '';
      const aspMatchesASCLeader = workOrder.ASC === asp;
  
      if (usertype === 'TPV Technician') {
        return workshopMatches && aspMatchesTPVTechnician;
      } else if (usertype === 'ASC Leader' || usertype === 'ASC Technician') {
        return aspMatchesASCLeader;
      } else {
        return true;
      }
    });

    const enriched = filtered.map(workOrder => {

      function parseCustomDate(dateStr) {
        if (!dateStr) return null;
        const [d, m, yTime] = dateStr.split('.');
        if (!yTime) return null;
        const [y, Time] = yTime.trim().split(' ');
        return new Date(`${y}-${m}-${d}T${Time || "00:00:00"}`);
      }

      const createDate = parseCustomDate(workOrder.OrderDate);
      const history = workOrder.history || [];

      const parsedActions = history.map(entry => {
        if (typeof entry !== "string") return null;
        const parts = entry.split("|");
        const obj = {};
        for (let i = 0; i < parts.length; i += 2) {
          obj[parts[i].trim()] = parts[i + 1]?.trim();
        }
        return {
          ActionCode: obj["Action"],
          ActionDate: parseCustomDate(obj["ChgDate"]),
        };
      }).filter(a => a && a.ActionCode && a.ActionDate);
      
      let tatDays = "N/A";
      
      if (!isNaN(createDate)) {
        const sortedActions = parsedActions.sort((a, b) => a.ActionDate - b.ActionDate);
        
        let effectiveStart = createDate;
        let effectiveEnd = null;
        let skipStart = null;
        let skipDays = 0;
        let orderClosed = false;

        for (let i = 0; i < sortedActions.length; i++) {
          const { ActionCode, ActionDate } = sortedActions[i];

          if (["117", "176", "190", "191", "192", "193", "194", "195", "196", "197", "336", "340", "357", "361", "392", "282", "288"].includes(ActionCode)) {
            effectiveEnd = ActionDate;
            orderClosed = true;
            break;
          }

          if (ActionCode === "351") {
            if (!skipStart){
              skipStart = new Date(ActionDate);
              effectiveEnd = ActionDate;
            }
            continue;
          }

          // 🔥 FIX #1 (Safe guard added)
          if (skipStart && ActionDate) {
            const skipStartDate = new Date(skipStart.toDateString());
            const skipEndDate = new Date(ActionDate.toDateString());
            const diff = Math.max(0, Math.floor((skipEndDate - skipStartDate) / (1000 * 60 * 60 * 24)));
            skipDays += diff;
            skipStart = null;
          }

          effectiveEnd = ActionDate;
        }

        // 🔥 FIX #2 (Safe guard added)
        if (skipStart) {
          const skipStartDate = new Date(skipStart.toDateString());
          const skipEndDate = new Date(new Date().toDateString());
          const diff = Math.max(
            0,
            Math.floor((skipEndDate - skipStartDate) / (1000 * 60 * 60 * 24))
          );
          skipDays += diff;
          skipStart = null;
        }

        // If still no end, fallback to last action or today
        if (!orderClosed) {
          effectiveEnd = new Date();
        } else if (!effectiveEnd) {
          effectiveEnd = sortedActions.length > 0
            ? sortedActions[sortedActions.length - 1].ActionDate
            : new Date();
        }

        // 🔥 FIX #3 (Safe guard added)
        if (effectiveStart && effectiveEnd) {
          const start = new Date(effectiveStart.toDateString());
          const end = new Date(effectiveEnd.toDateString());
          const totalDays = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
          tatDays = Math.max(0, totalDays - skipDays);
        } else {
          tatDays = "N/A";
        }
      }

      return {
        ...workOrder,
        PlannedDate: extractPlannedDate(workOrder),
        TAT: tatDays
      };
    });

    setFilteredData(enriched.slice(0, endIndex));
  }, [infotipData, endIndex]);
  

  const sortData = (key, direction) => {
    setSortConfig({ key, direction });
    const sorted = [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredData(sorted);
  };

  function openTabAndChangeColor(workOrderNumber) {
    document.getElementById(workOrderNumber).style.color = "blue";
    openTab(workOrderNumber);
  }

  const columns = [
    { label: 'Seq#', key: 'seq', disableSort: true  },
    { label: 'WO Date & Time', key: 'OrderDate' },
    { label: 'Infotip WO number', key: 'OrderName' },
    { label: 'Handling Type', key: 'HandlingType' },
    { label: 'Planned Date', key: 'PlannedDate' , disableSort: true },
    { label: 'Product Name', key: 'ProductName' },
    { label: 'Serial Number', key: 'SerialNumber' },
    { label: 'PostCode Country', key: 'Postcode', disableSort: true },
    { label: 'Workshop', key: 'WorkshopName' },
    { label: 'Infotip Status', key: 'OrderState' },
    { label: 'Creator Role', key: 'CreatorRole' },
    { label: 'Type of Order', key: 'TypeOfOrder' },
    { label: 'RR', key: 'RR' },
    { label: 'TAT', key: 'TAT' },
  ];

  return (
    <div className="wo-infotip">
      <h6>WO from Infotip</h6>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", margin: "10px" }}>
          <CircularProgress />
        </div>
      ) : filteredData.length > 0 ? (
        <table>
          <thead>
            <tr className="table-header-row">
              {columns.map((col, idx) => (
                <th key={idx} style={{ cursor: col.disableSort ? 'default' : 'pointer', padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>{col.label}</span>
                    {!col.disableSort && (
                      <div style={{ display: 'flow', marginLeft: '4px' }}>
                        <ArrowDropUpIcon
                          onClick={() => sortData(col.key, 'asc')}
                          style={{ cursor: 'pointer', color: sortConfig.key === col.key && sortConfig.direction === 'asc' ? '#337ab7' : 'grey' }}
                        />
                        <ArrowDropDownIcon
                          onClick={() => sortData(col.key, 'desc')}
                          style={{ cursor: 'pointer', color: sortConfig.key === col.key && sortConfig.direction === 'desc' ? '#337ab7' : 'grey' }}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              <td colSpan="15">
                <hr />
              </td>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((workOrder, i) => {
              const pageSeq = (currentPage - 1) * itemsPerPage;
              return (
                <tr className="table-body-row" key={i}>
                  <td>{pageSeq + i + 1}</td>
                  <td>{workOrder.OrderDate}</td>
                  <td className="uline curPo" onClick={() => openTabAndChangeColor(`${workOrder.OrderName}`)} id={`${workOrder.OrderName}`}>{workOrder.OrderName}</td>
                  <td>{workOrder.HandlingType}</td>
                  <td>{workOrder.PlannedDate}</td>
                  <td>{workOrder.ProductName}</td>
                  <td>{workOrder.SerialNumber}</td>
                  <td>{workOrder.Postcode} {workOrder.City} {workOrder.CountryId}</td>
                  <td>{workOrder.WorkshopName}</td>
                  <td className={typeof workOrder.OrderState === 'string' ? (workOrder.OrderState === "96" ? "green-status" : "red-status") : workOrder.OrderState}>{workOrder.OrderState}</td>
                  <td>{workOrder.CreatorRole}</td>
                  <td>{workOrder.TypeOfOrder}</td>
                  <td>{workOrder.RR}</td>
                  <td>{workOrder.TAT}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          No data available
        </div>
      )}
      <hr style={{ color: "rgb(60, 60, 255)" }}></hr>
    </div>
  );
}

export default WoInfotipData;
